/**
 * Admin API — Dashboard에서 사용하는 관리 엔드포인트
 *
 * /_admin/tables      → 테이블 목록 + 스키마
 * /_admin/data/:table → 테이블 데이터 CRUD
 * /_admin/functions   → 등록된 query/mutation 목록
 * /_admin/functions/details → query/mutation 실행용 메타데이터
 * /_admin/logs        → 로그 조회
 * /_admin/files       → 파일 목록
 * /_admin/scheduler   → 스케줄러 상태
 * /_admin/status      → 서버 상태
 * /_admin/usage       → Usage Metering (크레딧/사용량, Platform 프록시)
 */
import { Hono } from "hono";
import type { GencowCtx } from "@gencow/core";
import {
  assertAdminWriteBody,
  buildAdminCountStatement,
  buildAdminDeleteStatement,
  buildAdminInsertStatement,
  buildAdminSelectStatement,
  buildAdminUpdateStatement,
  loadAdminTableSchema,
  normalizeAdminLimit,
  normalizeAdminOffset,
} from "./admin-sql-guard.js";
import { applyAdminAccessMiddleware, type AdminAccessPolicy } from "./server-admin-access.js";
import { registerAdminFileRoutes } from "./server-admin-files.js";
import {
  createAdminLogsHandler,
  detectLogSource,
  isVerboseLogEntry,
  subscribeToLogs,
  unsubscribeFromLogs,
} from "./server-admin-logs.js";
import { registerAdminMaintenanceRoutes } from "./server-admin-maintenance.js";
import { redactAdminLogValue } from "./server-admin-redaction.js";
import type { SchedulerInfo } from "./scheduler.js";
import { collectAdminFunctionDetails } from "./admin-function-details.js";
import type { AdminFunctionDetailsResponse } from "./admin-function-details.js";
import { collectRuntimeOperationInventory } from "./runtime-operation-inventory.js";
import type { RuntimeOperationInventory } from "./runtime-operation-inventory.js";

type RawSqlFn = (sql: string, params?: any[]) => Promise<any[]>;
export type SchedulerDiagnosticsSource = {
  getSchedulerInfo?: () => SchedulerInfo;
};

export function collectAdminInvalidationQueryKeys(
  table: string,
  inventory: RuntimeOperationInventory,
): string[] {
  return [...inventory.legacyQueries, ...inventory.procedureQueries]
    .map((operation) => operation.key)
    .filter((key) => key.includes(table));
}

export function createAdminRoutes(
  db: any,
  rawSql: RawSqlFn,
  driver: "pglite" | "postgres",
  getCtx: () => GencowCtx,
  storage?: any,
  functionsPath?: string,
  options?: {
    accessPolicy?: AdminAccessPolicy;
    scheduler?: SchedulerDiagnosticsSource | null;
    getOperationInventory?: () => RuntimeOperationInventory;
    getFunctionDetails?: () => AdminFunctionDetailsResponse;
  },
) {
  const admin = new Hono();
  applyAdminAccessMiddleware(admin, options?.accessPolicy);
  const getOperationInventory = options?.getOperationInventory ?? (() => collectRuntimeOperationInventory());
  const getFunctionDetails = options?.getFunctionDetails ?? (() => collectAdminFunctionDetails());

  /**
   * After any write, push updated data via realtime emit.
   * Dashboard 데이터 편집 시 구독 중인 클라이언트에게 알림.
   */
  async function notifyTableChanged(table: string) {
    const related = collectAdminInvalidationQueryKeys(table, getOperationInventory());
    if (related.length > 0) {
      try {
        const ctx = getCtx();
        for (const queryKey of related) {
          // 전체 테이블 데이터 re-fetch는 불필요 — 클라이언트가 refetch하도록 빈 신호
          ctx.realtime.emit(queryKey, null);
        }
      } catch (e) {
        console.warn("[admin] realtime push failed:", e);
      }
    }
  }

  // ─── Tables: 테이블 목록 + 스키마 정보 + FK 관계 ─────
  admin.get("/tables", async (c) => {
    // Database-per-Tenant: 모든 테이블이 앱 전용 DB의 public 스키마에 위치
    const schemaName = "public";

    const rows = await rawSql(
      `
      SELECT table_name, column_name, data_type, is_nullable, column_default
      FROM information_schema.columns
      WHERE table_schema = $1
      ORDER BY table_name, ordinal_position
    `,
      [schemaName],
    );

    // FK 정보 조회 — 이 스키마의 모든 FK 제약 조건
    let fkRows: any[] = [];
    try {
      fkRows = await rawSql(
        `
        SELECT
          kcu.table_name   AS source_table,
          kcu.column_name  AS source_column,
          ccu.table_name   AS target_table,
          ccu.column_name  AS target_column
        FROM information_schema.table_constraints AS tc
        JOIN information_schema.key_column_usage AS kcu
          ON tc.constraint_name = kcu.constraint_name
          AND tc.table_schema = kcu.table_schema
        JOIN information_schema.constraint_column_usage AS ccu
          ON tc.constraint_name = ccu.constraint_name
          AND tc.table_schema = ccu.table_schema
        WHERE tc.constraint_type = 'FOREIGN KEY'
          AND tc.table_schema = $1
      `,
        [schemaName],
      );
    } catch {
      /* FK 조회 실패 시 빈 배열 */
    }

    // FK map: { "session.user_id" → { table: "user", column: "id" } }
    const fkMap = new Map<string, { table: string; column: string }>();
    for (const fk of fkRows) {
      fkMap.set(`${fk.source_table}.${fk.source_column}`, {
        table: fk.target_table,
        column: fk.target_column,
      });
    }

    const tables: Record<string, any[]> = {};
    for (const r of rows) {
      const tableName = r.table_name;
      if (!tables[tableName]) tables[tableName] = [];
      const ref = fkMap.get(`${tableName}.${r.column_name}`);
      tables[tableName].push({
        column: r.column_name,
        type: r.data_type,
        nullable: r.is_nullable === "YES",
        default: r.column_default,
        ...(ref ? { references: ref } : {}),
      });
    }

    return c.json(tables);
  });

  // ─── Data: 테이블 데이터 조회 (페이지네이션) ──────────
  admin.get("/data/:table", async (c) => {
    const table = c.req.param("table");
    const limit = normalizeAdminLimit(c.req.query("limit"));
    const offset = normalizeAdminOffset(c.req.query("offset"));
    const orderBy = c.req.query("orderBy") || "id";
    const order = c.req.query("order") || "desc";

    try {
      const schema = await loadAdminTableSchema(rawSql, table);
      const select = buildAdminSelectStatement({ schema, orderBy, order, limit, offset });
      const count = buildAdminCountStatement(schema);
      const data = await rawSql(select.sql, select.params as any[]);
      const countRows = await rawSql(count.sql, count.params as any[]);

      return c.json({
        data,
        total: Number(countRows[0]?.count || 0),
        limit,
        offset,
      });
    } catch (error: any) {
      return c.json({ error: error.message }, 400);
    }
  });

  // ─── Data: 행 추가 ───────────────────────────────────
  admin.post("/data/:table", async (c) => {
    const table = c.req.param("table");

    try {
      const body = assertAdminWriteBody(await c.req.json());
      const schema = await loadAdminTableSchema(rawSql, table);
      const insert = buildAdminInsertStatement(schema, body);
      const rows = await rawSql(insert.sql, insert.params as any[]);
      await notifyTableChanged(schema.tableName);
      return c.json(rows[0], 201);
    } catch (error: any) {
      return c.json({ error: error.message }, 400);
    }
  });

  // ─── Data: 행 수정 ───────────────────────────────────
  admin.patch("/data/:table/:id", async (c) => {
    const table = c.req.param("table");
    const id = c.req.param("id");

    try {
      const body = assertAdminWriteBody(await c.req.json());
      const schema = await loadAdminTableSchema(rawSql, table);
      const update = buildAdminUpdateStatement(schema, body, id);
      const rows = await rawSql(update.sql, update.params as any[]);
      await notifyTableChanged(schema.tableName);
      return c.json(rows[0]);
    } catch (error: any) {
      return c.json({ error: error.message }, 400);
    }
  });

  // ─── Data: 행 삭제 ───────────────────────────────────
  admin.delete("/data/:table/:id", async (c) => {
    const table = c.req.param("table");
    const id = c.req.param("id");

    try {
      const schema = await loadAdminTableSchema(rawSql, table);
      const statement = buildAdminDeleteStatement(schema, id);
      await rawSql(statement.sql, statement.params as any[]);
      await notifyTableChanged(schema.tableName);
      return c.json({ success: true });
    } catch (error: any) {
      return c.json({ error: error.message }, 400);
    }
  });

  // ─── Functions: legacy + procedure query/mutation 목록 ───────────────────
  admin.get("/functions", (c) => {
    const inventory = getOperationInventory();
    const queries = inventory.legacyQueries.map((operation) => operation.key);
    const mutations = inventory.legacyMutations.map((operation) => ({
      name: operation.key,
      isPublic: operation.allowAnonymous,
    }));
    const procedureQueries = inventory.procedureQueries.map((operation) => operation.key);
    const procedureMutations = inventory.procedureMutations.map((operation) => operation.key);
    return c.json({
      queries,
      mutations,
      procedureQueries,
      procedureMutations,
      totals: {
        queries: inventory.totalQueries,
        mutations: inventory.totalMutations,
      },
    });
  });

  admin.get("/functions/details", (c) => {
    return c.json(getFunctionDetails());
  });

  admin.get("/logs", createAdminLogsHandler());
  registerAdminFileRoutes({ admin, rawSql, storage });

  admin.get("/scheduler", async (c) => {
    const info: SchedulerInfo = options?.scheduler?.getSchedulerInfo?.() ?? {
      crons: [],
      pendingJobs: [],
      failedJobs: [],
    };

    if (info.crons.length === 0 && functionsPath) {
      try {
        const { resolve } = await import("path");
        const { existsSync } = await import("fs");
        const cronsPath = resolve(functionsPath, "crons.ts");
        const cronsPathJs = resolve(functionsPath, "crons.js");
        const cronsFile = existsSync(cronsPath) ? cronsPath : existsSync(cronsPathJs) ? cronsPathJs : null;

        if (cronsFile) {
          const cronsModule = await import(cronsFile);
          const cronsBuilder = cronsModule.default;
          if (cronsBuilder?.getJobs) {
            const jobs = cronsBuilder.getJobs();
            info.crons = jobs.map((job: any) => ({
              name: job.name,
              pattern: job.pattern,
              registeredAt: "(declarative)",
            }));
          }
        }
      } catch (e: any) {
        console.error("[scheduler] Fallback crons.ts read failed:", e?.message);
      }
    }

    return c.json(info);
  });

  // ─── Auth: 등록된 유저 목록 (better-auth DB) ─────────
  admin.get("/auth/users", async (c) => {
    try {
      const search = c.req.query("search") || "";
      const page = Math.max(0, parseInt(c.req.query("page") || "0", 10));
      const limit = Math.min(200, Math.max(1, parseInt(c.req.query("limit") || "50", 10)));
      const offset = page * limit;

      // Build WHERE clause for search
      const whereClause = search ? `WHERE LOWER("name") LIKE LOWER($1) OR LOWER("email") LIKE LOWER($1)` : "";
      const searchParams = search ? [`%${search}%`] : [];

      const countResult = await rawSql(`SELECT COUNT(*) as "total" FROM "user" ${whereClause}`, searchParams);
      const total = parseInt(String((countResult as Array<{ total: string }>)[0]?.total || "0"), 10);

      const limitIndex = search ? 2 : 1;
      const users = await rawSql(
        `SELECT id, name, email, email_verified as "emailVerified", image, created_at as "createdAt", updated_at as "updatedAt" FROM "user" ${whereClause} ORDER BY created_at DESC LIMIT $${limitIndex} OFFSET $${limitIndex + 1}`,
        [...searchParams, limit, offset],
      );

      return c.json({ data: users, total, page, limit });
    } catch {
      // user 테이블이 아직 없는 경우
      return c.json({ data: [], total: 0, page: 0, limit: 50 });
    }
  });

  // ─── Status: 서버 상태 ────────────────────────────────
  admin.get("/status", async (c) => {
    // DB 버전 조회
    let dbVersion = "unknown";
    try {
      const vRows = await rawSql("SELECT version()");
      dbVersion = vRows[0]?.version || "unknown";
    } catch {
      /* PGlite에서 실패할 수 있음 */
    }

    // 설치된 확장 목록
    let extensions: { name: string; version: string }[] = [];
    try {
      const extRows = await rawSql("SELECT extname, extversion FROM pg_extension ORDER BY extname");
      extensions = extRows.map((r: Record<string, string>) => ({
        name: r.extname,
        version: r.extversion,
      }));
    } catch {
      /* pg_extension 조회 불가 시 빈 배열 */
    }

    return c.json({
      uptime: process.uptime(),
      memory: process.memoryUsage(),
      platform: {
        runtime:
          typeof globalThis.Bun !== "undefined"
            ? `Bun v${(globalThis.Bun as any).version}`
            : `Node.js ${process.version}`,
        db: driver === "postgres" ? "PostgreSQL" : "PGlite (embedded PostgreSQL)",
        dbVersion,
        extensions,
        framework: "Hono",
        orm: "Drizzle",
      },
    });
  });

  // ─── Usage: 크레딧/사용량 (Platform 프록시) ───────────
  // HMAC Internal Token으로 Platform의 읽기 전용 API 호출
  admin.get("/usage", async (c) => {
    const meteringUrl = process.env.GENCOW_METERING_URL;
    const internalToken = process.env.GENCOW_INTERNAL_TOKEN;

    // BaaS 모드가 아니면 (로컬 dev) 빈 응답
    if (!meteringUrl || !internalToken) {
      return c.json({
        available: false,
        message: "Usage metering is only available in deployed mode",
      });
    }

    try {
      // Blue-Green fallback: primary 실패 시 대체 슬롯 포트 시도
      let res = await fetch(`${meteringUrl}/internal/usage/by-token`, {
        headers: { "X-Internal-Token": internalToken },
      }).catch(() => null);

      if (!res || !res.ok) {
        const altUrl = process.env.GENCOW_METERING_URL_ALT;
        if (altUrl) {
          // .catch 필수: ALT URL도 연결 불가 시 uncaught exception 방지
          // @see docs/specs/spec-app-status-centralization.md §Phase 0
          res = await fetch(`${altUrl}/internal/usage/by-token`, {
            headers: { "X-Internal-Token": internalToken },
          }).catch(() => null);
        }
      }

      if (!res || !res.ok) throw new Error("Platform unreachable");

      const data = (await res.json()) as Record<string, unknown>;
      return c.json({ available: true, ...data });
    } catch (e: unknown) {
      const errMsg = redactAdminLogValue(e);
      console.error(
        `[admin/usage] Failed to fetch usage: ${errMsg} (primary=${redactAdminLogValue(meteringUrl)}, alt=${redactAdminLogValue(process.env.GENCOW_METERING_URL_ALT || "none")})`,
      );
      return c.json({ available: false, error: "Failed to fetch usage data" }, 500);
    }
  });

  // ── POST /_admin/restart — 서버 재시작 ──────────────────
  // 응답 후 500ms 지연 → process.exit(0)
  // 로컬: gencow dev가 핫 리로드로 재시작
  // BaaS: provisioner가 프로세스 재시작
  admin.post("/restart", async (c) => {
    setTimeout(() => {
      console.log("[admin] Restarting server...");
      process.exit(0);
    }, 500);
    return c.json({ ok: true, message: "Server restarting..." });
  });

  registerAdminMaintenanceRoutes({ admin, rawSql, getCtx, functionsPath });

  // NOTE: POST /update-env 엔드포인트는 제거됨 (2026-04-12).
  // 이미지 설정이 파일 기반(.image-config.json)으로 전환되어 env 전파 불필요.
  // 분석서: analysis-image-config-architecture.md

  return admin;
}
export { detectLogSource, isVerboseLogEntry, subscribeToLogs, unsubscribeFromLogs };
